import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import CTAButton from "./CTAButton";

const COLS = [
  {
    title: "Services",
    links: [
      ["Menu Photography", "/menu-photography"],
      ["Food & Beverage", "/food-photography"],
      ["Venue & Interior", "/venue-photography"],
      ["Brand & Campaign", "/brand-photography"],
      ["Packages", "/packages"],
    ],
  },
  {
    title: "Specialised",
    links: [
      ["Uber Eats Photography", "/ubereats-photography"],
      ["Cafe Photography", "/cafe-photography"],
      ["Google Business Photos", "/google-business-profile-photos"],
      ["Restaurant Reels", "/restaurant-reels"],
    ],
  },
  {
    title: "Tools",
    links: [
      ["Menu Shoot Cost Planner", "/menu-shoot-cost-planner"],
      ["Menu Shoot Planner", "/menu-shoot-planner"],
      ["Delivery Photo Audit", "/delivery-app-photo-audit"],
      ["Photo Needs Calculator", "/photo-needs-calculator"],
      ["Marketing Score", "/restaurant-marketing-score"],
    ],
  },
  {
    title: "Locations",
    links: [
      ["Melbourne Locations Hub", "/locations"],
      ["Melbourne CBD", "/restaurant-photographer/melbourne-cbd"],
      ["Footscray", "/restaurant-photographer/footscray"],
      ["Brunswick", "/restaurant-photographer/brunswick"],
      ["Dandenong", "/restaurant-photographer/dandenong"],
    ],
  },
  {
    title: "Cuisines",
    links: [
      ["Indian Restaurants", "/cuisine/indian-restaurants"],
      ["Pizza Restaurants", "/cuisine/pizza-restaurants"],
      ["Burger Restaurants", "/cuisine/burger-restaurants"],
      ["Cafés", "/cuisine/cafes"],
      ["Fine Dining", "/cuisine/fine-dining"],
    ],
  },
  {
    title: "Studio",
    links: [
      ["About", "/about"],
      ["Contact", "/contact"],
      ["Get a Quote", "/get-a-quote"],
      ["Journal", "/blog"],
    ],
  },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-ink text-bone overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-60 pointer-events-none" />

      <div className="relative mx-auto max-w-[1600px] px-6 lg:px-10 py-20 lg:py-28">
        {/* CTA Band */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-16 lg:pb-24 border-b border-bone/10">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
              [ 01 ] — Start an enquiry
            </p>
            <h2 className="font-display text-display-md leading-[0.95] tracking-editorial">
              Let&apos;s shoot something
              <br />
              <span className="italic font-light">your guests will remember.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end gap-6">
            <p className="font-sans text-sm leading-relaxed text-bone/70 max-w-md">
              Mobile photography for restaurants, cafés and hospitality
              businesses across Melbourne — from quick menu refreshes to full
              campaign libraries.
            </p>
            <div className="flex flex-wrap gap-3">
              <CTAButton variant="primary" size="md">Get A Shoot Quote</CTAButton>
              <CTAButton variant="ghost" size="md" arrow={false}>
                View Services
              </CTAButton>
            </div>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 pt-16">
          <div className="lg:col-span-12 grid grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12 pb-12 border-b border-bone/10 mb-2">
            <div className="col-span-2 lg:col-span-1 space-y-6">
              <div>
                <p className="font-display text-2xl tracking-editorial">
                  Restaurant<span className="italic font-light text-ember"> Photographer</span>
                </p>
                <p className="text-xs uppercase tracking-wider2 font-bold opacity-50 mt-2">
                  Restaurants · Cafés · Hospitality · Melbourne
                </p>
              </div>
              <ul className="space-y-3 text-sm text-bone/70">
                <li className="flex items-center gap-3"><MapPin size={14} strokeWidth={1.4} /> Mobile · Servicing all of Melbourne</li>
                <li className="flex items-center gap-3"><Phone size={14} strokeWidth={1.4} /> 0494 756 111</li>
                <li className="flex items-center gap-3"><Mail size={14} strokeWidth={1.4} /> hello@restaurantphotographer.com.au</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10">
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-4">
                {col.title}
              </p>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => {
                  const isHash = to.startsWith("/#") || to.startsWith("#");
                  const testId = `footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                  return (
                    <li key={label}>
                      {isHash ? (
                        <a
                          href={to}
                          className="text-sm text-bone/75 hover:text-bone transition-colors"
                          data-testid={testId}
                        >
                          {label}
                        </a>
                      ) : (
                        <Link
                          to={to}
                          className="text-sm text-bone/75 hover:text-bone transition-colors"
                          data-testid={testId}
                        >
                          {label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          </div>
        </div>

        {/* Editorial wordmark — oversized */}
        <div className="pt-20 lg:pt-28 overflow-hidden">
          <p
            aria-hidden
            className="font-display italic tracking-editorial leading-none text-bone/[0.06] select-none whitespace-nowrap"
            style={{ fontSize: "clamp(4rem, 18vw, 18rem)" }}
          >
            Hospitality.
          </p>
        </div>

        {/* Bottom rail */}
        <div className="mt-10 pt-6 border-t border-bone/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-x-6 gap-y-2">
            <p className="text-xs uppercase tracking-wider2 font-bold opacity-50">
              © {new Date().getFullYear()} Restaurant Photographer · Melbourne
            </p>
            <nav className="flex items-center gap-5 text-xs uppercase tracking-wider2 font-bold" aria-label="Legal">
              <Link
                to="/privacy"
                data-testid="footer-link-privacy"
                className="opacity-50 hover:opacity-100 hover:text-ember transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                data-testid="footer-link-terms"
                className="opacity-50 hover:opacity-100 hover:text-ember transition-colors"
              >
                Terms
              </Link>
            </nav>
          </div>
          <p
            className="text-xs uppercase tracking-wider2 font-bold opacity-60"
            data-testid="powered-by"
          >
            Website &amp; Marketing by{" "}
            <a
              href="https://grownomics.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ember hover:text-bone transition-colors"
            >
              Grownomics Digital Marketing Agency
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
